define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi",
	"csi/_base/underscore",
	"csi/comm",
	"csi/reflection",
	"csi/service/writerService",
	"csi/utility/arrayUtility",
	"csi/utility/coreUtility",
	"csi/xml"
], function(array, declare, lang, csi, _, comm, reflection, {writeAggregateStoreOperation}, {arrayFromValue}, {isNullOrUndefined}, xml) {

	return declare(null, {
		// Mixin only for use with csi.action.Controller

		// String: attribute id for mapped relationships
		idKey: null,

		// Boolean: during OnAction callback, indicates if node was selected or desselected
		m_bChecked: false,

		// Node: during OnAction callback, indicates node that was selected or desselected
		m_nodeChecked: null,

		// String: url of newly selected node if acting on ref attribute (matches csi/heading/AttributeController)
		newValue: null,

		/** 
		 * @property {boolean} queryOmitsCurrentValues - set to true if query for selections filters out all current values;
		 * this saves the controller from having to query all current values to populate form data and disable existing selections -
		 * use in combination with AggregateNew="1" indicating only new values are allowed.
		 * This is the equivalent of the returnCurrentValues setting on aggregate stores and can be removed once using aggregate stores.
		 */
		//queryOmitsCurrentValues: false,

		// String: when creating new nodes of AggregateClass, source can be specified as node to copy rather than create from scratch
		urlAggregateClassSource: null,

		// String[]: array of newly selected urls to append
		urlsAppended: null,

		// String[]: array of previously selected urls to remove
		urlsRemoved: null,

		// Action: action to use for popup view
		_actionViewParameters: null,

		/** @property {boolean} _appendedOnCreate - flag indicating new nodes have already been appended to the parent */
		_appendedOnCreate: false,

		// Boolean: indicates if operating on single multiplicity relationship (type ref or specific index into ref container)
		_bRef: false,

		// Boolean: indicates if operations should be output to writer
		_bWrite: true,

		// Object: map of keys/urls to insert if map relationship
		_keysAppended: null,

		/** @property {Record<cnl, cnl>} _mapAggregateReferences - map from referenced node to aggregate class node (if using aggregateClass) */
		_mapAggregateReferences: null,

		constructor: function() {
			this.urlsAppended = [];
			this.urlsRemoved = [];
		},

		getFormData: function() {
			// summary: configure form data for aggregate action
			if (this.m_formData) return this.m_formData;

			var action = this.m_action;
			var idAttr = action.aggregateId;
			var cache = this.m_cache;
			var fd = this.inherited(arguments);
			var node = this._getAggregateRootNode();
			var view = this.widget;
			var f;

			fd.m_bOmitSelections = action.bAggregateNew;

			if (action.bReverse) {
				var rc = reflection.getNodeClass(node);
				if (rc) {
					var bIncoming = true;
					var rcSource;

					if (action.heading) {
						idAttr = action.heading.m_id;
						bIncoming = false;
					} else if (action.aggregateClassIds) {
						idAttr = action.aggregateClassIds[0];
					}

					if (bIncoming) {
						rcSource = reflection.getClass(action.aggregateClass) || (action.m_relationship ? action.m_relationship.getTargetClass() : null);
					}

					var rcr = rc.getRelation(idAttr, true, true, bIncoming, rcSource);
					if (rcr) {
						var rcrReverse;

						if (action.heading) {
							// if heading aggregate, then heading id is already the reverse relation
							rcrReverse = rcr;
						} else {
							// use reverse relationship to determine multiplicity and allowed selections
							rcrReverse = rcr.bidirectionalRelation;
							if (rcrReverse) idAttr = rcrReverse.getId();
						}

						if (rcrReverse) {
							// use multiplicity of relation which is reverse relationship of target attribute to set
							fd.m_bSelectOne = rcrReverse.isSingle;
						} else {
							// if no reverse relationship, then if relationship is composite then it must be multiplicity 1;
							// otherwise assume multiple
							fd.m_bSelectOne = rcr && rcr.isComposite;
						}
					}
				}
			} else {
				// determine if only selecting one node rather than multiple
				fd.m_bSelectOne = this.isAggregateSingleSelection();
			}

			if (idAttr && !action.bAggregateAdd) {
				let aggregated = [];

				if (fd.m_bSelectOne) {
					const url = xml.nodeAttributeGetKeyValue(node, idAttr, this.keyContainer);
					const child = cache.getNode(url);
					if (child) {
						aggregated = [child];
					}
				} else {
					aggregated = _.compact(cache.getNodes(node[idAttr]));

					if (f = csi.resolveFunction(action.aggregateChildTest, view)) {
						const ctx = view.getContext();
						const filteredList = [];

						ctx.forEach(aggregated, (ctx) => {
							const child = ctx.getNode();
							if (f(ctx, child)) {
								filteredList.push(child);
							}
						});

						aggregated = filteredList;
					}
				}

				// map referenced nodes to aggregateClass nodes
				const {aggregateClassIds, flattenAggregateClassReferences} = action;
				const aggregateClassId = aggregateClassIds?.[0];

				if (flattenAggregateClassReferences) {
					this._mapAggregateReferences = {};
				}

				const {_mapAggregateReferences} = this;

				aggregated.forEach((n) => {
					if (_mapAggregateReferences) {
						const url = n[aggregateClassId];
						_mapAggregateReferences[url] = n.$URL;
						n = cache.get(url);
					}

					fd.addAggregatedNode(n);
				});
			}

			// Check whether user included criteria for when selection of data should be disabled
			if (f = csi.resolveFunction(action.aggregateTest, view)) {
				fd.m_fAggregateTest = f;
				fd.m_parentNode = node;
			}

			return fd;
		},

		getAggregatedAttributeType: function(/*Node*/node) {
			// summary: get the type of aggregated attribute from the given node
			var idAttr = this.getAction().aggregateId;
			var rca = reflection.getAttribute(node, idAttr);
			return rca ? rca.getType() : xml.nodeAttributeGetType(node, idAttr);
		},

		getAggregatedAttributeTypeFromParent: function() {
			// summary: get the type of aggregated attribute from the parent node
			return this.getAggregatedAttributeType(this.getParent());
		},

		/**
		 * Return urls references by aggregate relationship
		 * @return cnl[]
		 */
		getAggregateUrls() {
			const node = this._getAggregateRootNode();
			return arrayFromValue(node[this.m_action.aggregateId]);
		},

		/**
		 * Return true if attribute being edited is single, either because it is a ref, or we are editing a specific key of a refmap
		 * @return {boolean}
		 */
		isAggregateSingleSelection() {
			// TODO: this could potentially take advantage of updateOperation.singleChoice in aggregate store response, although
			// it does not yet support passing an index into a map to affect this and currentValues. We're also not guaranteed to
			// have the aggregate response yet.
			const node = this._getAggregateRootNode();
			const type = this.getAggregatedAttributeType(node);
			return (type === xml.ATTR_REF) || !isNullOrUndefined(this.keyContainer);
		},

		/**
		 * Extension point to add configuration to the view being opened in Go to Dialog
		 * @param {TableView} tableView
		 * @param {C8NodeType} nodeType
		 */
		onAggregateStoreTableView(tableView, nodeType) { },

		_getActionViewParameters: function(/*Context*/ctx, /*Heading*/h, /*Renderer?*/ren) {
			// summary: get parameters to override default action view
			var p;

			if (ren && ren.getStoreAction) {
				p = ren.getStoreAction(ctx, h);
			}

			if (ren && !p) {
				p = h.m_action;
			}

			this._actionViewParameters = p;
			return p;
		},

		_getAggregateRootNode: function() {
			// summary: if controller node does not exist, then use parent node (as in table AggregateFrom)
			return this.getNode() || this.getParent();
		},

		_getAggregateSelections: function(/*FormData*/fd) {
			// summary: return nodes newly selected in form data
			const {m_cache, _mapAggregateReferences} = this;
			const nodes = fd.getSelectedNodes();

			if (_mapAggregateReferences) {
				return nodes.map((node) => m_cache.get(_mapAggregateReferences[node.$URL]) || node);
			}

			return nodes;
		},

		_getAggregateRemovals: function(/*FormData*/fd) {
			// summary: return nodes deselected in form data
			const {m_cache, _mapAggregateReferences} = this;
			const nodes = fd.getRemovedNodes();

			if (_mapAggregateReferences) {
				return nodes.map((node) => m_cache.get(_mapAggregateReferences[node.$URL]));
			}

			return nodes;
		},

		_getFixedFilters: function() {
			// summary: return filters for popup view
			var a = this._actionViewParameters || this.m_action;
			return a && a.m_fixedQuery ? a.m_fixedQuery.getElements() : null;
		},

		_getViewFilters: function() {
			// summary: return filters for popup view
			var a = this._actionViewParameters || this.m_action;
			return a ? a.m_filtersView : null;
		},

		_preActionRequests: function(/*Request[]*/requests) {
			// summary: query for nodes already aggregated
			const {
				m_action: {aggregateId, aggregateClassIds, bReverse},
				m_cache,
				queryOmitsCurrentValues,
			} = this;

			// assumption is that cache is filled if selecting single node
			if (bReverse || queryOmitsCurrentValues || !aggregateId || this.isAggregateSingleSelection()) {
				return;
			}

			var node = this._getAggregateRootNode();
			var request = node && comm.getNodeRequest(node[aggregateId]);
			if (request) {
				if (aggregateClassIds) {
					request.addDependencyPaths([xml.path(aggregateClassIds[0])]);
				}

				// if nodes are already in cache then don't need to make request; don't worry about sending timestamp as these nodes are only
				// needed for pre-selection because of the way FormData works
				request = m_cache.reduceNodesRequest(request);

				if (request) {
					requests.push(request);
				}
			}
		},

		_hasSelections: function(/*FormData*/fd) {
			return fd.hasSelections();
		},

		/**
		 * Write operations to process aggregate selections, returning true if there are selections
		 * @param {FormData} fd
		 * @param {string} aggregateId
		 * @param {StoreNames} namesStore
		 * @return {boolean}
		 */
		_processAggregateStoreSelections(fd, aggregateId, namesStore) {
			if (!this._hasSelections(fd)) {
				return false;
			}

			const {
				m_idAttrParent,
				m_urlNode,
				m_urlParent,
				m_writer
			} = this;

			const {aggregateStoreResponse} = namesStore;
			const nodesAdded = this._getAggregateSelections(fd) || [];
			const nodesRemoved = this._getAggregateRemovals(fd) || [];
			const node = this.getNode() || this.getParent();

			this.urlsAppended = nodesAdded.map(xml.nodeGetURL);
			this.urlsRemoved = nodesRemoved.map(xml.nodeGetURL);

			// call operation to save all selections
			writeAggregateStoreOperation(m_writer, aggregateStoreResponse, this.urlsAppended, this.urlsRemoved);

			// still ensure OnAction called for each selection (and removal if not a single ref)
			this.m_idAttrParent = aggregateId;
			this.m_urlParent = node.$URL;

			nodesAdded.forEach((node) => {
				this.m_urlNode = node.$URL;
				this._processNode(node, true);
			});

			if (!aggregateStoreResponse.updateOperation.singleChoice) {
				nodesRemoved.forEach((node) => {
					this.m_urlNode = node.$URL;
					this._processNode(node, false);
				});
			}

			// restore original values
			this.m_urlNode = m_urlNode;
			this.m_urlParent = m_urlParent;
			this.m_idAttrParent = m_idAttrParent;
			this.m_bChecked = false;
			this.m_nodeChecked = null;

			return true;
		},

		_processFormData: function(/*FormData*/fd) {
			if (!this._hasSelections(fd)) {
				return false;
			}

			// temporarily change controller so that node being operated on becomes m_urlParent and
			// aggregated nodes will fill m_urlNode
			var action = fd.getAction();
			var urlParent = this.m_urlParent;
			var urlNode = this.m_urlNode;
			var idAttrParent = this.m_idAttrParent;
			var nodesAdded = this._getAggregateSelections(fd);
			var nodesRemoved = this._getAggregateRemovals(fd);
			var nodeRoot = this._getAggregateRootNode();

			this.m_idAttrParent = action.aggregateId;

			if (action.bReverse) {
				var nodeContext = nodeRoot;
				var typeAttr;

				this.m_urlNode = nodeRoot.$URL;

				// remove this node from relationship of each deselected node
				array.forEach(nodesRemoved, function(node) {
					this.m_urlParent = node.$URL;
					typeAttr = typeAttr || this.getAggregatedAttributeType(node);

					// if context class, then find context node for target
					if (action.aggregateClass) {
						nodeContext = fd.getAggregateNode(node, nodeRoot, this.m_cache);
					}

					this.urlsRemoved = [];
					this.urlsAppended = [];
					this._processChanges(typeAttr, null, [nodeContext]);
				}, this);

				// add this node to relationship for each selected node
				array.forEach(nodesAdded, function(node) {
					this.m_urlParent = node.$URL;
					typeAttr = typeAttr || this.getAggregatedAttributeType(node);
					this.urlsRemoved = [];
					this.urlsAppended = [];
					this._processChanges(typeAttr, [nodeRoot], null);
				}, this);

				if (nodesAdded) {
					this.urlsAppended = array.map(nodesAdded, xml.nodeGetURL);
				}

				if (nodesRemoved) {
					this.urlsRemoved = array.map(nodesRemoved, xml.nodeGetURL);
				}
			} else {
				this.m_urlParent = xml.isValidURL(urlNode) ? urlNode : urlParent;
				this.m_urlNode = null;

				// process changes individually
				this._processChanges(this.getAggregatedAttributeTypeFromParent(), nodesAdded, nodesRemoved);

				// process changes with single server request
				if (this._bWrite && action.bOneRequest) {
					this.toWriterRequest()
						.addParameter(xml.PROP_AGGREGATE_ADDS, xml.ATTR_REFLIST, this.urlsAppended)
						.addParameter(xml.PROP_AGGREGATE_REMOVES, xml.ATTR_REFLIST, this.urlsRemoved);
				}

				// validate new nodes if aggregateClass and not several nodes are aggregated at once (bOneRequest)
				if (action.aggregateClass && !action.bOneRequest) {
					array.forEach(this.urlsAppended, function(url) {
						this.validateNode(url, false);
					}, this);
				}
			}

			// cleanup
			this.m_urlNode = urlNode;
			this.m_urlParent = urlParent;
			this.m_idAttrParent = idAttrParent;
			this.m_bChecked = false;
			this.m_nodeChecked = null;
			this._appendedOnCreate = false;

			return true;
		},

		_processChanges: function(/*String*/typeAttr, /*Node[]*/nodesAppend, /*Node[]*/nodesRemove) {
			// summary: process changes for a single parent node with possible multiple additions/removals
			var bWrite = this._bWrite;

			if ((typeAttr == xml.ATTR_REF) || (this.keyContainer != null)) {
				this._bRef = true;
				this._processChangesRef(typeAttr, nodesAppend, nodesRemove);
				this._bRef = false;
			} else {
				this._processChangesContainer(typeAttr, nodesAppend, nodesRemove);
			}

			// restore previous setting
			this._bWrite = bWrite;
		},

		_processChangesContainer: function(/*String*/typeAttr, /*Node[]*/nodesAppend, /*Node[]*/nodesRemove) {
			// summary: process changes for a single parent node with possible multiple additions/removals
			var action = this.getAction();
			var id = action.aggregateId;

			// turn off writing for each individual node if sending as one request
			if (action.bOneRequest && !action.bReverse) {
				this._bWrite = false;
			}

			// process each node selected
			if (nodesAppend) {
				this.urlsAppended = array.map(nodesAppend, lang.hitch(this, this._processAppendNode));
			}

			// process each node removed
			if (nodesRemove) {
				this.urlsRemoved = array.map(nodesRemove, lang.hitch(this, this._processRemoveNode));
			}

			// change parent references according to change in selections
			if (id && this._bWrite && !action.m_request) {
				var cn = this.m_writer.changeNode(this.m_urlParent);
				var add = this.urlsAppended;
				var rem = this.urlsRemoved;
				var bChange;

				if (add && add.length) {
					if (this._keysAppended) {
						cn.changeAttributeKeys(id, typeAttr, this._keysAppended, true);
					} else if (!this._appendedOnCreate) {
						cn.changeAttribute(id, typeAttr, add, true);
					}
					bChange = true;
				}

				// if nodes are deleted (if aggregateClass), then references will be cleaned up automatically
				if (rem && rem.length && !action.aggregateClass) {
					cn.changeAttribute(id, typeAttr, rem, false, true);
					bChange = true;
				}

				// update flags if changed
				if (bChange) {
					var fg = xml.ATTR_FLAG_MODIFIED_BY_CLIENT;

					if (xml.nodeAttributeHasFlag(this.getParent(), id, xml.ATTR_FLAG_HAS_EXPRESSION)) {
						fg |= xml.ATTR_FLAG_DISABLE_EXPRESSION;
					}

					cn.changeAttributeFlag(id, fg, true);
				}
			}
		},

		_processChangesRef: function(/*String*/typeAttr, /*Node[]*/nodesAppend, /*Node[]*/nodesRemove) {
			// summary: process changes for a single parent node with possible multiple additions/removals
			var action = this.getAction();

			// process selected node
			if (nodesAppend && nodesAppend.length) {
				this.urlsAppended = array.map(nodesAppend, lang.hitch(this, this._processAppendNode));
				this.newValue = this.urlsAppended[0];
			} else {
				// call OnAction callback when no selection; there could be an existing aggregation though if a wizard
				// and the selected node is kept the same
				const {newValue} = this;
				this.m_urlNode = this.newValue = (Array.isArray(newValue) ? newValue[0] : newValue) || csi.EMPTY_URL;
				this._processNode(null, true);

				// write request to reset relationship if no selection
				if (this._bWrite && action.m_request) {
					this.toWriterRequest().addParameter(xml.PROP_AGGREGATE, xml.ATTR_BOOLEAN, true);
				}
			}

			// process removed node to delete node in context (if valid aggregateClass)
			if (nodesRemove && nodesRemove.length) {
				this.urlsRemoved = array.map(nodesRemove, lang.hitch(this, this._processRemoveNode));
			}

			// change parent references according to change in selections
			if (this._bWrite && !action.m_request) {
				this._processRef(typeAttr);
			}
		},

		_processRef: function(/*String*/typeAttr) {
			// summary: isolate setting of ref value into another function so it can be overwritten by inline aggregate action
			var action = this.getAction();
			var id = action.aggregateId;
			var cn = this.m_writer.changeNode(this.m_urlParent).changeAttributeFlag(id, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);

			if (this.keyContainer == null) {
				if (xml.nodeAttributeHasExpression(this.getParent(), id, true)) {
					cn.disableAttributeExp(id);
				}

				cn.changeAttribute(id, typeAttr, this.newValue);
			} else {
				cn.changeAttributeKeys(id, typeAttr, csi.objectNew(this.keyContainer, this.newValue));
			}

			return cn;
		},

		_processNode: function(/*Node*/node, /*Boolean*/bChecked) {
			// summary: process OnAction for appended/removed node
			this.m_bChecked = bChecked;
			this.m_nodeChecked = node;

			var value = this._onAction();
			if (value === null) {
				throw new Error('AGGREGATE');
			} else if (value && (value != csi.EMPTY_URL)) {
				return value;
			}

			return this.m_urlNode;
		},

		_processAppendNode: function(/*Node*/node) {
			// summary: write operations for appended node
			var action = this.getAction();
			var type = action.aggregateClass;
			var bRequest = !!action.m_request;
			var createNode;

			this.m_urlNode = node.$URL;

			// cache selected node
			this.m_cache.cacheNode(node);

			// write operations
			if (this._bWrite) {
				// create node to wrap aggregated node
				if (type) {
					this.m_urlNode = comm.createUrl();
				}

				if (bRequest) {
					// add request parameter for aggregate action
					this.toWriterRequest()
						.addParameter(xml.PROP_AGGREGATE, xml.ATTR_BOOLEAN, true)
						.addParameter(xml.PROP_URL_FROM, xml.ATTR_REF, node.$URL);
				} else if (type) {
					var w = this.m_writer;
					var cn;

					if (this.urlAggregateClassSource) {
						w.copyNode(this.m_urlNode, this.urlAggregateClassSource);
						cn = w.changeNode(this.m_urlNode);
					} else {
						createNode = cn = w.createNode(this.m_urlNode, type);
					}

					array.forEach(action.aggregateClassIds, function(id) {
						cn.changeAttribute(id, xml.ATTR_REF, node.$URL);
					});
				}

				this.toWriterFields(this.m_urlNode);
			}

			var urlAppend = this._processNode(node, true);

			// track mapping of key to url if map relationship
			if (this._bWrite && !bRequest) {
				if (this.idKey) {
					this._keysAppended = this._keysAppended || {};
					this._keysAppended[node[this.idKey]] = this.m_urlNode;
				} else if (createNode && action.aggregateId) {
					// if creating a new node, then call Attach operation as part of CreateNode so that the node
					// is already attached to the parent by the time any OnCreate customer actions are invoked
					createNode.attach(this.m_urlParent, action.aggregateId);
					this._appendedOnCreate = true;
				}
			}

			return urlAppend;
		},

		_processRemoveNode: function(/*Node*/node) {
			// summary: write operations for removed node
			var action = this.getAction();

			this.m_urlNode = node.$URL;

			// write operations
			if (this._bWrite) {
				if (action.m_request) {
					// call server requests for ref attributes only on new value, not old value
					if (!this._bRef) {
						this.toWriterRequest().addParameter(xml.PROP_AGGREGATE, xml.ATTR_BOOLEAN, false);
					}
				} else if (action.aggregateClass) {
					this.m_writer.deleteNode(this.m_urlNode);
				}
			}

			// call OnAction callback for ref attributes only on newly selected node, not previously selected node
			return this._bRef ? this.m_urlNode : this._processNode(node, false);
		}
	});
});